/**
 * Life map → geometry. No React, no DOM writes, no colours.
 *
 * Three things here are what make the drawing readable rather than a hairball:
 *
 *  1. Nodes are sized to their own titles. The ReactFlow canvas used a fixed
 *     200px box and truncated everything past it, so a map of "Get the visa
 *     paperwork…" told the user nothing. Here a box grows to fit two wrapped
 *     lines and only then ellipsises.
 *
 *  2. Each dream tree is laid out on its own and the finished trees are packed
 *     into rows. Handing one big disconnected graph to dagre lets unrelated
 *     dreams interleave their columns, which is exactly why the old canvas
 *     needed panning to understand. Packing separately also means a tree's
 *     bounding box is honest, so we can draw a frame around it.
 *
 *  3. Parent→child links are rounded elbows sharing a trunk, and cross-links
 *     (blocks, derived-from, "at this place") bow away as separate curves.
 *     Mixing the two on the same geometry is what made the old view look like
 *     wiring rather than a plan.
 */
import dagre from '@dagrejs/dagre'
import type { GraphNode } from '../../services/hierarchyGraphService'
import { TYPE_DEPTH, type GraphModel } from './graphModel'
import { textWidth, wrapText } from './measure'

// ── box metrics ─────────────────────────────────────────────────────────────

const PAD_X = 13
const PAD_TOP = 9
const PAD_BOTTOM = 10
const ICON_W = 16
const ICON_GAP = 9
const KIND_LINE_H = 12
const TITLE_LINE_H = 16.5
const SUB_LINE_H = 13
const MIN_W = 158
const MAX_W = 252
const MAX_TITLE_LINES = 2

const TITLE_SIZE = 13
const KIND_SIZE = 9
const SUB_SIZE = 10

/**
 * Everything the renderer needs to place glyphs inside a box it did not
 * measure. Exported so the drawing cannot drift from the sizing — a box laid
 * out for two title lines and drawn with three is a clipped title.
 */
export const boxMetrics = {
  padX: PAD_X,
  padTop: PAD_TOP,
  iconW: ICON_W,
  kindLineH: KIND_LINE_H,
  titleLineH: TITLE_LINE_H,
  subLineH: SUB_LINE_H,
  titleSize: TITLE_SIZE,
  kindSize: KIND_SIZE,
  subSize: SUB_SIZE,
  /** Left edge of the text column. */
  textX: PAD_X + ICON_W + ICON_GAP,
  radius: 11,
}

export const fonts = (family: string) => ({
  title: `600 ${TITLE_SIZE}px ${family}`,
  kind: `600 ${KIND_SIZE}px ${family}`,
  sub: `italic ${SUB_SIZE}px ${family}`,
  edgeLabel: `500 10px ${family}`,
  frame: `600 12px ${family}`,
})

/**
 * How a tree is arranged.
 *
 *  branches — ranks down the page, siblings across. The default, and the one
 *             that reads most like a plan.
 *  roadmap  — ranks left to right. Depth grows along the axis titles are
 *             already wide on, so deep chains stay compact and legible.
 *  constellation — the root at the centre with each generation on a ring.
 *             Shows the shape of a whole dream at a glance; less good for
 *             following one chain step by step.
 */
export type MapShape = 'branches' | 'roadmap' | 'constellation'

export interface LayoutOptions {
  /** CSS font-family the diagram will render with — measurement must match. */
  fontFamily: string
  shape?: MapShape
  /** Vertical gap between ranks. */
  rankSep?: number
  /** Horizontal gap between siblings. */
  nodeSep?: number
  /** Draw a frame per dream tree (only meaningful with more than one tree). */
  frames?: boolean
  /**
   * Width:height of the pane the map will be read in. Trees are packed to
   * roughly this shape so a wide screen gets a wide poster instead of a tall
   * column with empty gutters either side.
   */
  aspect?: number
  /** Overrides `aspect` when a specific wrap width is wanted. */
  targetWidth?: number
}

export interface LaidOutNode {
  key: string
  node: GraphNode
  isGhost: boolean
  x: number
  y: number
  w: number
  h: number
  titleLines: string[]
  kindLabel: string
  /** "also linked to: …" when the node's owner differs from the parent drawn. */
  subtitle: string | null
  rootKey: string
  depth: number
  /** Centre of this node's constellation, when that is the shape in use. */
  hub?: { x: number; y: number }
}

export interface LaidOutEdge {
  id: string
  path: string
  kind: 'structural' | 'semantic'
  relationship: string
  sourceKey: string
  targetKey: string
  label: string | null
  labelX: number
  labelY: number
}

export interface TreeFrame {
  rootKey: string
  title: string
  x: number
  y: number
  w: number
  h: number
}

export interface Ring {
  cx: number
  cy: number
  r: number
}

export interface Scene {
  shape: MapShape
  nodes: LaidOutNode[]
  byKey: Map<string, LaidOutNode>
  structural: LaidOutEdge[]
  cross: LaidOutEdge[]
  frames: TreeFrame[]
  /** Generation guides, drawn under a constellation. Empty for other shapes. */
  rings: Ring[]
  width: number
  height: number
}

const humanKind = (type: string): string => type.replace(/_/g, ' ')

// ── measuring ───────────────────────────────────────────────────────────────

interface Box {
  w: number
  h: number
  titleLines: string[]
  kindLabel: string
  subtitle: string | null
}

function measureNode(
  n: GraphNode,
  parentRef: { id: number; type: string } | null,
  f: ReturnType<typeof fonts>,
  maxW = MAX_W,
): Box {
  const title = (n.title || '').trim() || '(untitled)'
  const kindLabel = humanKind(n.type).toUpperCase()

  const owner = n.primary_parent
  const isAdopted = !!(
    owner &&
    (!parentRef || owner.id !== parentRef.id || owner.type !== parentRef.type) &&
    !(owner.id === n.id && owner.type === n.type)
  )
  const subtitle = isAdopted && owner ? `also linked to: ${owner.title}` : null

  const chromeW = PAD_X * 2 + ICON_W + ICON_GAP
  const textMax = maxW - chromeW
  const wrapped = wrapText(title, f.title, textMax, MAX_TITLE_LINES)

  let contentW = Math.max(wrapped.width, textWidth(kindLabel, f.kind))
  if (subtitle) {
    // The subtitle may ellipsise on its own; it must never widen the box.
    contentW = Math.max(contentW, Math.min(textWidth(subtitle, f.sub), textMax))
  }

  const w = Math.min(maxW, Math.max(Math.min(MIN_W, maxW), Math.ceil(chromeW + contentW)))
  const h =
    PAD_TOP +
    KIND_LINE_H +
    wrapped.lines.length * TITLE_LINE_H +
    (subtitle ? SUB_LINE_H : 0) +
    PAD_BOTTOM

  return { w, h: Math.ceil(h), titleLines: wrapped.lines, kindLabel, subtitle }
}

// ── connected components over the tree skeleton ─────────────────────────────

/**
 * Split on structural edges only. Two dreams joined by a "blocks" cross-link
 * are still two dreams as far as the user is concerned, and merging them would
 * drag both into one column stack.
 */
function components(model: GraphModel): string[][] {
  const adj = new Map<string, string[]>()
  const push = (a: string, b: string) => {
    const list = adj.get(a)
    if (list) list.push(b)
    else adj.set(a, [b])
  }
  for (const e of model.structural) {
    push(e.sourceKey, e.targetKey)
    push(e.targetKey, e.sourceKey)
  }

  const seen = new Set<string>()
  const out: string[][] = []
  for (const n of model.nodes) {
    if (seen.has(n.key)) continue
    const group: string[] = []
    const stack = [n.key]
    seen.add(n.key)
    while (stack.length) {
      const cur = stack.pop()!
      group.push(cur)
      for (const next of adj.get(cur) ?? []) {
        if (seen.has(next)) continue
        seen.add(next)
        stack.push(next)
      }
    }
    out.push(group)
  }
  return out
}

// ── placement, per shape ────────────────────────────────────────────────────

type Pos = { x: number; y: number }

interface TreePlacement {
  pos: Map<string, Pos>
  w: number
  h: number
  /** Origin of the arrangement, in the same normalised space as `pos`. */
  hub?: Pos
  /** Ring radii, for the shapes that have rings to draw. */
  rings?: number[]
}

/**
 * Shift a placement so its bounding box starts at (0, 0) and report the size.
 * `also` widens the box beyond the nodes themselves, which the ring guides need
 * — a ring runs all the way round even where no node sits on it, and a frame
 * that only fits the nodes would cut it.
 */
function normalise(
  pos: Map<string, Pos>,
  boxes: Map<string, Box>,
  hub?: Pos,
  rings?: number[],
  also?: { minX: number; minY: number; maxX: number; maxY: number },
): TreePlacement {
  let minX = also?.minX ?? Infinity
  let minY = also?.minY ?? Infinity
  let maxX = also?.maxX ?? -Infinity
  let maxY = also?.maxY ?? -Infinity
  for (const [key, p] of pos) {
    const b = boxes.get(key)!
    minX = Math.min(minX, p.x)
    minY = Math.min(minY, p.y)
    maxX = Math.max(maxX, p.x + b.w)
    maxY = Math.max(maxY, p.y + b.h)
  }
  for (const [key, p] of pos) pos.set(key, { x: p.x - minX, y: p.y - minY })
  return {
    pos,
    w: maxX - minX,
    h: maxY - minY,
    hub: hub ? { x: hub.x - minX, y: hub.y - minY } : undefined,
    rings,
  }
}

function placeLayered(
  keys: string[],
  model: GraphModel,
  boxes: Map<string, Box>,
  rankdir: 'TB' | 'LR',
  rankSep: number,
  nodeSep: number,
): TreePlacement {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir,
    ranksep: rankSep,
    nodesep: nodeSep,
    edgesep: 16,
    ranker: 'network-simplex',
    marginx: 0,
    marginy: 0,
  })

  const inGroup = new Set(keys)
  for (const key of keys) {
    const b = boxes.get(key)!
    g.setNode(key, { width: b.w, height: b.h })
  }
  for (const e of model.structural) {
    if (inGroup.has(e.sourceKey) && inGroup.has(e.targetKey)) {
      g.setEdge(e.sourceKey, e.targetKey)
    }
  }
  dagre.layout(g)

  const pos = new Map<string, Pos>()
  for (const key of keys) {
    const b = boxes.get(key)!
    const p = g.node(key)
    pos.set(key, { x: (p?.x ?? 0) - b.w / 2, y: (p?.y ?? 0) - b.h / 2 })
  }
  return normalise(pos, boxes)
}

const RING_GAP = 40

/**
 * The root at the centre, each generation on a ring, siblings sharing their
 * parent's slice of the circle in proportion to how much hangs off them.
 *
 * Ring radius is not a fixed step: a ring has to be far enough out that the
 * arc each node owns is at least as long as the node is wide, or the boxes
 * collide. Solving that per ring is what keeps a dream with nine goals from
 * drawing them on top of each other.
 */
function placeRadial(
  keys: string[],
  model: GraphModel,
  boxes: Map<string, Box>,
  rootKey: string,
): TreePlacement {
  const inGroup = new Set(keys)
  const children = new Map<string, string[]>()
  const addChild = (parent: string, child: string) => {
    const list = children.get(parent)
    if (list) list.push(child)
    else children.set(parent, [child])
  }
  for (const key of keys) {
    if (key === rootKey) continue
    const p = model.byKey.get(key)?.parentKey
    addChild(p && inGroup.has(p) && p !== key ? p : rootKey, key)
  }

  // Breadth-first from the root. `seen` also breaks any cycle the structural
  // parent chain managed to contain; whatever it cuts off is reattached to the
  // root on the next sweep so no node is silently dropped.
  const depth = new Map<string, number>([[rootKey, 0]])
  const byDepth: string[][] = [[rootKey]]
  const seen = new Set([rootKey])
  for (;;) {
    const queue = [...seen]
    while (queue.length) {
      const cur = queue.shift()!
      const d = depth.get(cur)!
      for (const c of children.get(cur) ?? []) {
        if (seen.has(c)) continue
        seen.add(c)
        depth.set(c, d + 1)
        ;(byDepth[d + 1] ??= []).push(c)
        queue.push(c)
      }
    }
    const stranded = keys.filter((k) => !seen.has(k))
    if (stranded.length === 0) break
    for (const k of stranded) {
      children.set(k, (children.get(k) ?? []).filter((c) => !stranded.includes(c)))
      addChild(rootKey, k)
    }
  }

  const weight = new Map<string, number>()
  const weigh = (key: string): number => {
    const cached = weight.get(key)
    if (cached !== undefined) return cached
    const kids = children.get(key) ?? []
    const total = kids.length === 0 ? 1 : kids.reduce((s, c) => s + weigh(c), 0)
    weight.set(key, total)
    return total
  }
  weigh(rootKey)

  const angle = new Map<string, number>()
  const span = new Map<string, number>()
  const assign = (key: string, from: number, to: number) => {
    angle.set(key, (from + to) / 2)
    span.set(key, to - from)
    const kids = children.get(key) ?? []
    const total = kids.reduce((s, c) => s + weigh(c), 0) || 1
    let a = from
    for (const c of kids) {
      const width = (to - from) * (weigh(c) / total)
      assign(c, a, a + width)
      a += width
    }
  }
  // Start the sweep at the top of the circle.
  assign(rootKey, -Math.PI / 2, Math.PI * 1.5)

  // Boxes stay axis-aligned while the rings are circular, so how much room a
  // box eats depends on where on the ring it sits: one at three o'clock
  // presents its WIDTH to the ring above, one at twelve presents its height.
  // Projecting the half-extents onto the radial and tangential directions is
  // what stops a chain of only-children from stacking on top of itself.
  const radialExtent = (key: string, d: number) => {
    const b = boxes.get(key)!
    // The root sits on the hub, so it faces every direction at once.
    if (d === 0) return Math.max(b.w, b.h) / 2
    const a = angle.get(key) ?? 0
    return (b.w / 2) * Math.abs(Math.cos(a)) + (b.h / 2) * Math.abs(Math.sin(a))
  }
  const tangentialExtent = (key: string) => {
    const b = boxes.get(key)!
    const a = angle.get(key) ?? 0
    return (b.w / 2) * Math.abs(Math.sin(a)) + (b.h / 2) * Math.abs(Math.cos(a))
  }
  const deepestAt = (d: number) =>
    Math.max(...(byDepth[d] ?? []).map((k) => radialExtent(k, d)), 0)

  const radii = [0]
  for (let d = 1; d < byDepth.length; d++) {
    let r = radii[d - 1] + RING_GAP + deepestAt(d - 1) + deepestAt(d)
    for (const key of byDepth[d]) {
      const s = span.get(key) ?? 0
      // Arc length this node owns must cover its footprint along the ring.
      if (s > 0.0001) r = Math.max(r, (tangentialExtent(key) * 2 + 16) / s)
    }
    radii[d] = r
  }

  const pos = new Map<string, Pos>()
  for (const key of keys) {
    const b = boxes.get(key)!
    const r = radii[depth.get(key) ?? 0] ?? 0
    const a = angle.get(key) ?? 0
    pos.set(key, {
      x: r * Math.cos(a) - b.w / 2,
      y: r * Math.sin(a) - b.h / 2,
    })
  }
  const outer = radii[radii.length - 1] ?? 0
  return normalise(pos, boxes, { x: 0, y: 0 }, radii.slice(1), {
    minX: -outer,
    minY: -outer,
    maxX: outer,
    maxY: outer,
  })
}

// ── edge geometry ───────────────────────────────────────────────────────────

const R = 13

/**
 * Where a ray aimed at a box's centre from `from` crosses its border. Curves
 * that connect centres would run underneath the boxes and put their arrowheads
 * out of sight, so both ends get trimmed to the border instead.
 */
function borderPoint(n: LaidOutNode, from: Pos): Pos {
  const cx = n.x + n.w / 2
  const cy = n.y + n.h / 2
  const dx = cx - from.x
  const dy = cy - from.y
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return { x: cx, y: cy }
  const t = Math.min(
    dx === 0 ? Infinity : (n.w / 2 + 1) / Math.abs(dx),
    dy === 0 ? Infinity : (n.h / 2 + 1) / Math.abs(dy),
  )
  return { x: cx - dx * t, y: cy - dy * t }
}

/**
 * Parent bottom → child top as a rounded elbow. Siblings share the horizontal
 * run, which is what reads as a trunk with branches rather than N diagonals.
 */
function elbowDown(s: LaidOutNode, t: LaidOutNode): string {
  const sx = s.x + s.w / 2
  const sy = s.y + s.h
  const tx = t.x + t.w / 2
  // Stop a hair short so the arrowhead sits on the border, not inside it.
  const ty = t.y - 1

  if (Math.abs(tx - sx) < 2) return `M ${sx} ${sy} L ${tx} ${ty}`

  const mid = sy + (ty - sy) / 2
  const sgn = tx > sx ? 1 : -1
  const r = Math.min(R, Math.abs(tx - sx) / 2, Math.abs(ty - sy) / 2)

  return [
    `M ${sx} ${sy}`,
    `L ${sx} ${mid - r}`,
    `Q ${sx} ${mid} ${sx + sgn * r} ${mid}`,
    `L ${tx - sgn * r} ${mid}`,
    `Q ${tx} ${mid} ${tx} ${mid + r}`,
    `L ${tx} ${ty}`,
  ].join(' ')
}

/** The same elbow turned on its side, for the roadmap shape. */
function elbowAcross(s: LaidOutNode, t: LaidOutNode): string {
  const sx = s.x + s.w
  const sy = s.y + s.h / 2
  const tx = t.x - 1
  const ty = t.y + t.h / 2

  if (Math.abs(ty - sy) < 2) return `M ${sx} ${sy} L ${tx} ${ty}`

  const mid = sx + (tx - sx) / 2
  const sgn = ty > sy ? 1 : -1
  const r = Math.min(R, Math.abs(ty - sy) / 2, Math.abs(tx - sx) / 2)

  return [
    `M ${sx} ${sy}`,
    `L ${mid - r} ${sy}`,
    `Q ${mid} ${sy} ${mid} ${sy + sgn * r}`,
    `L ${mid} ${ty - sgn * r}`,
    `Q ${mid} ${ty} ${mid + r} ${ty}`,
    `L ${tx} ${ty}`,
  ].join(' ')
}

/**
 * A ring-to-ring link, bent around the hub so it follows the circle rather
 * than cutting a chord across it — the bundled look that makes a radial
 * hierarchy readable instead of a starburst of straight spokes.
 */
function radialLink(s: LaidOutNode, t: LaidOutNode): string {
  const hub = s.hub ?? t.hub
  const sc = { x: s.x + s.w / 2, y: s.y + s.h / 2 }
  const tc = { x: t.x + t.w / 2, y: t.y + t.h / 2 }
  if (!hub) {
    const a = borderPoint(s, tc)
    const b = borderPoint(t, sc)
    return `M ${a.x} ${a.y} L ${b.x} ${b.y}`
  }

  const sr = Math.hypot(sc.y - hub.y, sc.x - hub.x)
  const tr = Math.hypot(tc.y - hub.y, tc.x - hub.x)
  const ta = Math.atan2(tc.y - hub.y, tc.x - hub.x)
  // A node sitting on the hub has no meaningful angle of its own; borrow the
  // child's so the link leaves the centre straight.
  const sa = sr < 1 ? ta : Math.atan2(sc.y - hub.y, sc.x - hub.x)

  let delta = ta - sa
  while (delta > Math.PI) delta -= Math.PI * 2
  while (delta < -Math.PI) delta += Math.PI * 2

  const ma = sa + delta / 2
  const mr = (sr + tr) / 2
  const control = { x: hub.x + mr * Math.cos(ma), y: hub.y + mr * Math.sin(ma) }

  const a = borderPoint(s, control)
  const b = borderPoint(t, control)
  return `M ${a.x} ${a.y} Q ${control.x} ${control.y} ${b.x} ${b.y}`
}

/**
 * Cross-links leave from the side facing their partner and bow outward, with
 * each repeat of the same pair bowing a little further so parallel links stay
 * countable instead of stacking into one thick line.
 */
function crossPath(
  s: LaidOutNode,
  t: LaidOutNode,
  repeat: number,
): { path: string; labelX: number; labelY: number } {
  const toRight = t.x + t.w / 2 >= s.x + s.w / 2
  const sx = toRight ? s.x + s.w : s.x
  const tx = toRight ? t.x : t.x + t.w
  const sy = s.y + s.h / 2
  const ty = t.y + t.h / 2

  const dist = Math.hypot(tx - sx, ty - sy)
  const bow = Math.min(190, Math.max(46, dist * 0.34)) + repeat * 22
  const sgn = toRight ? 1 : -1

  const c1x = sx + sgn * bow
  const c2x = tx - sgn * bow
  const path = `M ${sx} ${sy} C ${c1x} ${sy} ${c2x} ${ty} ${tx} ${ty}`

  // Cubic midpoint (t = 0.5).
  return {
    path,
    labelX: (sx + 3 * c1x + 3 * c2x + tx) / 8,
    labelY: (sy + 3 * sy + 3 * ty + ty) / 8,
  }
}

const CROSS_LABEL: Record<string, string> = {
  blocks: 'blocks',
  blocked_by: 'blocks',
  derived_from: 'from',
  location: 'at',
  planned: 'planned',
}

// ── the pass ────────────────────────────────────────────────────────────────

export function layoutLifeMap(model: GraphModel, opts: LayoutOptions): Scene {
  const f = fonts(opts.fontFamily)
  const shape = opts.shape ?? 'branches'
  // Left-to-right needs less between ranks (the gap reads as a corridor, not a
  // squeeze) and more between siblings, because siblings stack vertically there.
  const rankSep = opts.rankSep ?? (shape === 'roadmap' ? 62 : 74)
  const nodeSep = opts.nodeSep ?? (shape === 'roadmap' ? 22 : 34)

  if (model.nodes.length === 0) {
    return {
      shape,
      nodes: [],
      byKey: new Map(),
      structural: [],
      cross: [],
      frames: [],
      rings: [],
      width: 0,
      height: 0,
    }
  }

  // A ring has to be pushed out far enough to fit the widest box on it, so a
  // constellation pays for width twice: once in the box and again in the radius
  // of every ring beyond it. Narrower boxes buy a much tighter figure.
  const maxBoxW = shape === 'constellation' ? 196 : undefined

  const boxes = new Map<string, Box>()
  for (const m of model.nodes) {
    const parentRef = m.parentKey ? model.byKey.get(m.parentKey)?.node ?? null : null
    boxes.set(
      m.key,
      measureNode(
        m.node,
        parentRef ? { id: parentRef.id, type: parentRef.type } : null,
        f,
        maxBoxW,
      ),
    )
  }

  // 1. Lay each tree out on its own, in local coordinates.
  interface Placed extends TreePlacement {
    keys: string[]
    rootKey: string
    rootTitle: string
    rootDepth: number
  }

  const placed: Placed[] = components(model).map((keys) => {
    // The tree's own root: the node with no structural parent, or failing that
    // the shallowest type present.
    const rootKey =
      keys.find((k) => !model.byKey.get(k)?.parentKey) ??
      [...keys].sort(
        (a, b) =>
          (TYPE_DEPTH[model.byKey.get(a)!.node.type] ?? 99) -
          (TYPE_DEPTH[model.byKey.get(b)!.node.type] ?? 99),
      )[0]

    const placement =
      shape === 'constellation'
        ? placeRadial(keys, model, boxes, rootKey)
        : placeLayered(
            keys,
            model,
            boxes,
            shape === 'roadmap' ? 'LR' : 'TB',
            rankSep,
            nodeSep,
          )

    const rootNode = model.byKey.get(rootKey)!.node
    return {
      ...placement,
      keys,
      rootKey,
      rootTitle: (rootNode.title || '').trim() || 'Untitled',
      rootDepth: TYPE_DEPTH[rootNode.type] ?? 99,
    }
  })

  // 2. Pack the trees into rows: dreams first, wrapping at a width chosen to
  //    match the pane's shape. Rows are top-aligned so rank 0 lines up.
  const GAP_X = 78
  const GAP_Y = 108
  const PAD = 20
  const CAPTION = 26

  // A lone node is not a tree and framing it just boxes a label around its own
  // title. Only real branching gets a frame, and only when there is more than
  // one thing on the map to tell apart.
  const framed = (t: Placed) => (opts.frames ?? true) && placed.length > 1 && t.keys.length > 1
  const padOf = (t: Placed) => (framed(t) ? PAD : 0)
  const capOf = (t: Placed) => (framed(t) ? CAPTION : 0)

  const order = [...placed].sort(
    (a, b) =>
      a.rootDepth - b.rootDepth ||
      b.keys.length - a.keys.length ||
      a.rootTitle.localeCompare(b.rootTitle),
  )

  let targetWidth = opts.targetWidth ?? 0
  if (!targetWidth) {
    // Area of the packed result, gaps included, solved for the wanted aspect.
    const area = order.reduce(
      (sum, t) =>
        sum +
        (t.w + padOf(t) * 2 + GAP_X) * (t.h + padOf(t) * 2 + capOf(t) + GAP_Y),
      0,
    )
    const widest = Math.max(...order.map((t) => t.w + padOf(t) * 2))
    targetWidth = Math.max(widest, Math.sqrt(area * (opts.aspect ?? 1.7)))
  }

  const offsets = new Map<Placed, { x: number; y: number }>()
  let cursorX = 0
  let rowY = 0
  let rowH = 0
  let sceneW = 0

  for (const tree of order) {
    const pad = padOf(tree)
    const outerW = tree.w + pad * 2
    if (cursorX > 0 && cursorX + outerW > targetWidth) {
      rowY += rowH + GAP_Y
      cursorX = 0
      rowH = 0
    }
    offsets.set(tree, { x: cursorX + pad, y: rowY + pad + capOf(tree) })
    cursorX += outerW + GAP_X
    rowH = Math.max(rowH, tree.h + pad * 2 + capOf(tree))
    sceneW = Math.max(sceneW, cursorX - GAP_X)
  }
  const sceneH = rowY + rowH

  // 3. Absolute nodes.
  const nodes: LaidOutNode[] = []
  for (const tree of order) {
    const off = offsets.get(tree)!
    const hub = tree.hub
      ? { x: Math.round(off.x + tree.hub.x), y: Math.round(off.y + tree.hub.y) }
      : undefined
    for (const key of tree.keys) {
      const m = model.byKey.get(key)!
      const b = boxes.get(key)!
      const p = tree.pos.get(key)!
      nodes.push({
        key,
        node: m.node,
        isGhost: m.isGhost,
        x: Math.round(off.x + p.x),
        y: Math.round(off.y + p.y),
        w: b.w,
        h: b.h,
        titleLines: b.titleLines,
        kindLabel: b.kindLabel,
        subtitle: b.subtitle,
        rootKey: tree.rootKey,
        depth: TYPE_DEPTH[m.node.type] ?? 99,
        hub,
      })
    }
  }
  const byKey = new Map(nodes.map((n) => [n.key, n]))

  // 4. Edges.
  const link =
    shape === 'constellation'
      ? radialLink
      : shape === 'roadmap'
        ? elbowAcross
        : elbowDown

  const structural: LaidOutEdge[] = []
  for (const e of model.structural) {
    const s = byKey.get(e.sourceKey)
    const t = byKey.get(e.targetKey)
    if (!s || !t) continue
    structural.push({
      id: e.id,
      path: link(s, t),
      kind: 'structural',
      relationship: e.relationship,
      sourceKey: e.sourceKey,
      targetKey: e.targetKey,
      label: null,
      labelX: 0,
      labelY: 0,
    })
  }

  const repeats = new Map<string, number>()
  const cross: LaidOutEdge[] = []
  for (const e of model.cross) {
    const s = byKey.get(e.sourceKey)
    const t = byKey.get(e.targetKey)
    if (!s || !t) continue
    const pk = `${e.sourceKey}|${e.targetKey}`
    const repeat = repeats.get(pk) ?? 0
    repeats.set(pk, repeat + 1)
    const geom = crossPath(s, t, repeat)
    cross.push({
      id: e.id,
      path: geom.path,
      kind: 'semantic',
      relationship: e.relationship,
      sourceKey: e.sourceKey,
      targetKey: e.targetKey,
      label: CROSS_LABEL[e.relationship] ?? humanKind(e.relationship),
      labelX: geom.labelX,
      labelY: geom.labelY,
    })
  }

  // 5. Frames. Packing guarantees these cannot overlap, which is the whole
  //    reason the trees were laid out separately.
  const frames: TreeFrame[] = order.filter(framed).map((tree) => {
    const off = offsets.get(tree)!
    return {
      rootKey: tree.rootKey,
      title: tree.rootTitle,
      x: off.x - PAD,
      y: off.y - PAD - CAPTION,
      w: tree.w + PAD * 2,
      h: tree.h + PAD * 2 + CAPTION,
    }
  })

  const rings: Ring[] = []
  for (const tree of order) {
    const off = offsets.get(tree)!
    if (!tree.hub || !tree.rings) continue
    for (const r of tree.rings) {
      rings.push({ cx: off.x + tree.hub.x, cy: off.y + tree.hub.y, r })
    }
  }

  return {
    shape,
    nodes,
    byKey,
    structural,
    cross,
    frames,
    rings,
    width: Math.ceil(sceneW),
    height: Math.ceil(sceneH),
  }
}
